import React from 'react';

import dayjs from '~/timeInit';

import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import { getTargetIdValue } from './changeMetadata';
import { STATUS_CHIP_COLOR } from './constants';
import {
  REQUEST_TYPE_DETAIL_LABELS,
  getExtranetApprovalLabel,
  getExtranetApprovalSectionLabel,
} from './extranetApprovalLabels';
import type { ChangeMetadata } from './types';
import ValueDiff from './valueDiff/ValueDiff';

interface ChangeDiffDialogProps {
  change: ExtranetPendingChange | null;
  metadata?: ChangeMetadata;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

export default function ChangeDiffDialog({ change, metadata, onClose, onApprove, onReject }: ChangeDiffDialogProps) {
  const diffSectionLabel = change ? getExtranetApprovalSectionLabel(change.target_ids) : undefined;
  const propertyId = change ? getTargetIdValue(change.target_ids, 'property_id') : '';

  return (
    <Dialog open={!!change} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Change diff — {change && getExtranetApprovalLabel(change)}</DialogTitle>
      {change && (
        <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Partner
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {metadata?.vendorName ?? change.fk_vendor_id}
            </Typography>
            {metadata?.vendorName && (
              <Typography variant="caption" color="text.secondary">
                {change.fk_vendor_id}
              </Typography>
            )}
          </Box>
          {propertyId && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Property
              </Typography>
              <Typography variant="body2">{metadata?.propertyName ?? propertyId}</Typography>
              {metadata?.propertyName && (
                <Typography variant="caption" color="text.secondary">
                  {propertyId}
                </Typography>
              )}
            </Box>
          )}
          {(metadata?.roomName || metadata?.ratePlanName) && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Room
              </Typography>
              <Typography variant="body2">
                {[metadata.roomName, metadata.ratePlanName].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          )}
          {diffSectionLabel && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Section
              </Typography>
              <Typography variant="body2">{diffSectionLabel}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Change type
            </Typography>
            <Typography variant="body2">
              {REQUEST_TYPE_DETAIL_LABELS[change.request_type] ?? change.request_type}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Submitted
            </Typography>
            <Typography variant="body2">{dayjs(change.created_at).format('D/M/YY, h:mm a')}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Changed by
            </Typography>
            <Typography variant="body2">{metadata?.requestedByEmail ?? change.requested_by_user_id}</Typography>
            {metadata?.requestedByName && (
              <Typography variant="caption" color="text.secondary">
                {metadata.requestedByName}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip size="small" label={change.status} color={STATUS_CHIP_COLOR[change.status]} />
            </Box>
          </Box>
          {change.reviewed_by_user_id && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {change.status === 'approved' ? 'Approved by' : 'Reviewed by'}
              </Typography>
              <Typography variant="body2">{metadata?.reviewedByEmail ?? change.reviewed_by_user_id}</Typography>
              {metadata?.reviewedByName && (
                <Typography variant="caption" color="text.secondary">
                  {metadata.reviewedByName}
                </Typography>
              )}
            </Box>
          )}
          {change.reviewed_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Reviewed at
              </Typography>
              <Typography variant="body2">{dayjs(change.reviewed_at).format('D/M/YY, h:mm a')}</Typography>
            </Box>
          )}
        </Box>
      )}
      <DialogContent dividers>
        {change && <ValueDiff previous={change.previous_value} proposed={change.proposed_value} />}
      </DialogContent>
      <DialogActions>
        {change?.status === 'pending' && (
          <>
            <Button variant="contained" onClick={() => onApprove(change.id)}>
              Approve
            </Button>
            <Button variant="text" color="error" onClick={() => onReject(change.id)}>
              Reject
            </Button>
          </>
        )}
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
